import {
  Building2,
  Check,
  Copy,
  ExternalLink,
  Mail,
  MapPin,
  Phone,
  Printer,
  Send,
  User,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { MakeCallModal } from '@/components/common/calls/make-call-modal/MakeCallModal';
import { SendFrontEmailDialog } from '@/components/matter/SendFrontEmailDialog';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AdjusterDetail,
  ContactHandleSchema,
  CoverageSchema,
  PolicyType,
} from '@/services/types/coverage-types';

import { benefitTypeLabels } from './constants';

interface CoverageContactModalProps {
  open: boolean;
  onClose: () => void;
  coverage: CoverageSchema;
  matterId: string;
}

const getPolicyTypeLabel = (policyType: PolicyType | null) => {
  switch (policyType) {
    case PolicyType.AUTO:
      return 'Auto';
    case PolicyType.HEALTH:
      return 'Health';
    case PolicyType.HOMEOWNERS:
      return 'Homeowners';
    case PolicyType.COMMERCIAL:
      return 'Commercial';
    case PolicyType.UMBRELLA:
      return 'Umbrella';
    default:
      return 'Policy';
  }
};

const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

const getHandleIcon = (handleType: string) => {
  const type = handleType.toLowerCase();
  if (type.includes('phone') || type.includes('mobile')) {
    return <Phone className="h-3.5 w-3.5 text-zinc-400" />;
  }
  if (type.includes('email')) {
    return <Mail className="h-3.5 w-3.5 text-zinc-400" />;
  }
  if (type.includes('fax')) {
    return <Printer className="h-3.5 w-3.5 text-zinc-400" />;
  }
  if (
    type.includes('web') ||
    type.includes('portal') ||
    type.includes('site')
  ) {
    return <ExternalLink className="h-3.5 w-3.5 text-zinc-400" />;
  }
  if (type.includes('address')) {
    return <MapPin className="h-3.5 w-3.5 text-zinc-400" />;
  }
  return <Phone className="h-3.5 w-3.5 text-zinc-400" />;
};

const getHandleTypeLabel = (handleType: string): string => {
  const type = handleType.toLowerCase();
  if (type.includes('work_phone')) return 'Work Phone';
  if (type.includes('mobile')) return 'Mobile';
  if (type.includes('phone')) return 'Phone';
  if (type.includes('work_email')) return 'Work Email';
  if (type.includes('email')) return 'Email';
  if (type.includes('fax')) return 'Fax';
  if (type.includes('portal')) return 'Claims Portal';
  if (type.includes('web') || type.includes('site')) return 'Website';
  if (type.includes('address')) return 'Address';
  return handleType;
};

const isPhoneHandle = (handleType: string): boolean => {
  const type = handleType.toLowerCase();
  return type.includes('phone') || type.includes('mobile');
};

const isEmailHandle = (handleType: string): boolean => {
  const type = handleType.toLowerCase();
  return type.includes('email');
};

const isWebHandle = (handleType: string): boolean => {
  const type = handleType.toLowerCase();
  return (
    type.includes('web') || type.includes('portal') || type.includes('site')
  );
};

interface CopyButtonProps {
  value: string;
  label: string;
}

const CopyButton = ({ value, label }: CopyButtonProps) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`Copied ${label}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded transition-colors"
      title={`Copy ${label}`}
    >
      {copied ? (
        <Check className="h-3 w-3 text-emerald-600" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
};

interface ContactHandleRowProps {
  handle: ContactHandleSchema;
  onPhoneClick: (phone: string, label: string) => void;
  onEmailClick: (email: string) => void;
  onWebClick: (url: string) => void;
}

const ContactHandleRow = ({
  handle,
  onPhoneClick,
  onEmailClick,
  onWebClick,
}: ContactHandleRowProps) => {
  const icon = getHandleIcon(handle.handle_type);
  const label = handle.name || getHandleTypeLabel(handle.handle_type);
  const isPhone = isPhoneHandle(handle.handle_type);
  const isEmail = isEmailHandle(handle.handle_type);
  const isWeb = isWebHandle(handle.handle_type);

  return (
    <div className="group flex items-center justify-between py-1.5 hover:bg-zinc-50 px-2 -mx-2 rounded transition-colors">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {icon}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide">
            {label}
          </div>
          <div className="text-xs text-zinc-800 font-mono truncate">
            {isPhone ? formatPhoneNumber(handle.value) : handle.value}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <CopyButton value={handle.value} label={label} />
        {isPhone && (
          <button
            type="button"
            onClick={() => onPhoneClick(handle.value, label)}
            className="p-1 text-violet-500 hover:text-violet-700 hover:bg-violet-50 rounded transition-colors"
            title="Call"
          >
            <Phone className="h-3 w-3" />
          </button>
        )}
        {isEmail && (
          <button
            type="button"
            onClick={() => onEmailClick(handle.value)}
            className="p-1 text-violet-500 hover:text-violet-700 hover:bg-violet-50 rounded transition-colors"
            title="Send Email"
          >
            <Send className="h-3 w-3" />
          </button>
        )}
        {isWeb && (
          <button
            type="button"
            onClick={() => onWebClick(handle.value)}
            className="p-1 text-violet-500 hover:text-violet-700 hover:bg-violet-50 rounded transition-colors"
            title="Open"
          >
            <ExternalLink className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

interface AdjusterSectionProps {
  adjuster: AdjusterDetail;
  onPhoneClick: (phone: string, label: string) => void;
  onEmailClick: (email: string) => void;
  onWebClick: (url: string) => void;
}

const AdjusterSection = ({
  adjuster,
  onPhoneClick,
  onEmailClick,
  onWebClick,
}: AdjusterSectionProps) => {
  const handles = adjuster.handles || [];

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-50">
          <User className="h-3.5 w-3.5 text-violet-600" />
        </div>
        <div>
          <div className="text-sm font-semibold text-zinc-900">
            {adjuster.name || 'Unknown Adjuster'}
          </div>
          {adjuster.benefit_type && (
            <div className="text-[10px] text-zinc-500">
              {benefitTypeLabels[adjuster.benefit_type]}
            </div>
          )}
        </div>
      </div>

      {handles.length > 0 ? (
        <div className="space-y-0.5">
          {handles.map((handle, idx) => (
            <ContactHandleRow
              key={`${handle.handle_type}-${idx}`}
              handle={handle}
              onPhoneClick={onPhoneClick}
              onEmailClick={onEmailClick}
              onWebClick={onWebClick}
            />
          ))}
        </div>
      ) : (
        <div className="text-xs text-zinc-400 italic">
          No contact information
        </div>
      )}
    </div>
  );
};

export const CoverageContactModal = ({
  open,
  onClose,
  coverage,
  matterId,
}: CoverageContactModalProps) => {
  const [callModalOpen, setCallModalOpen] = React.useState(false);
  const [callModalData, setCallModalData] = React.useState<{
    phoneNumber: string;
    contactLabel: string;
  } | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [emailDialogData, setEmailDialogData] = React.useState<{
    email: string;
  } | null>(null);

  const handlePhoneClick = React.useCallback(
    (phoneNumber: string, contactLabel: string) => {
      setCallModalData({
        phoneNumber,
        contactLabel: `${coverage.carrier?.name || 'Coverage'} - ${contactLabel}`,
      });
      setCallModalOpen(true);
    },
    [coverage.carrier?.name],
  );

  const handleEmailClick = React.useCallback((email: string) => {
    setEmailDialogData({ email });
    setEmailDialogOpen(true);
  }, []);

  const handleWebClick = React.useCallback((url: string) => {
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = `https://${url}`;
    }
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const carrierHandles = coverage.carrier?.handles || [];
  const adjusters = coverage.adjusters || [];
  const hasCarrierContacts =
    carrierHandles.length > 0 || coverage.carrier?.address;
  const hasAdjusters = adjusters.length > 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
          {/* Header */}
          <DialogHeader className="px-4 pt-4 pb-3 border-b border-zinc-100">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-50">
                <Building2 className="h-5 w-5 text-violet-600" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-base font-semibold text-zinc-900 truncate">
                  {coverage.carrier?.name || 'Unknown Carrier'}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge
                    variant="secondary"
                    className="text-[10px] font-normal bg-zinc-100 text-zinc-600 border-0 px-1.5 py-0 h-4"
                  >
                    {getPolicyTypeLabel(coverage.policy_type)}
                  </Badge>
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="max-h-[60vh] overflow-y-auto">
            {/* Carrier Contacts */}
            {hasCarrierContacts && (
              <div className="px-4 py-3 border-b border-zinc-100">
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                  Carrier Contact
                </div>
                <div className="space-y-0.5">
                  {carrierHandles.map((handle, idx) => (
                    <ContactHandleRow
                      key={`carrier-${handle.handle_type}-${idx}`}
                      handle={handle}
                      onPhoneClick={handlePhoneClick}
                      onEmailClick={handleEmailClick}
                      onWebClick={handleWebClick}
                    />
                  ))}
                  {coverage.carrier?.address && (
                    <div className="group flex items-center justify-between py-1.5 hover:bg-zinc-50 px-2 -mx-2 rounded transition-colors">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <MapPin className="h-3.5 w-3.5 text-zinc-400" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide">
                            Address
                          </div>
                          <div className="text-xs text-zinc-800">
                            {coverage.carrier.address}
                          </div>
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <CopyButton
                          value={coverage.carrier.address}
                          label="Address"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Adjusters */}
            {hasAdjusters && (
              <div className="px-4 py-3">
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                  Adjusters ({adjusters.length})
                </div>
                <div className="divide-y divide-zinc-100">
                  {adjusters.map((adjuster, idx) => (
                    <AdjusterSection
                      key={`adjuster-${idx}`}
                      adjuster={adjuster}
                      onPhoneClick={handlePhoneClick}
                      onEmailClick={handleEmailClick}
                      onWebClick={handleWebClick}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* No contacts */}
            {!hasCarrierContacts && !hasAdjusters && (
              <div className="px-4 py-8 text-center">
                <div className="text-zinc-400 mb-1">
                  <User className="h-8 w-8 mx-auto" />
                </div>
                <div className="text-sm text-zinc-500">
                  No contact information available
                </div>
                <div className="text-xs text-zinc-400 mt-1">
                  Add adjusters or carrier contacts to see them here
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Phone Call Modal */}
      <MakeCallModal
        isOpen={callModalOpen}
        onClose={() => {
          setCallModalOpen(false);
          setCallModalData(null);
        }}
        initialPhoneNumber={callModalData?.phoneNumber}
        initialContactLabel={callModalData?.contactLabel}
        matterId={matterId}
        source="coverage_contact_modal"
      />

      {/* Email Dialog */}
      <SendFrontEmailDialog
        open={emailDialogOpen}
        onClose={() => {
          setEmailDialogOpen(false);
          setEmailDialogData(null);
        }}
        matterId={matterId}
        defaultTo={emailDialogData?.email ? [emailDialogData.email] : []}
      />
    </>
  );
};
